import React, { useState, useMemo } from 'react';
import { useLocationContext } from '../../hooks/useLocationContext';
import { ScopedLink } from '../../components/ScopedLink';
import ConfirmDialog from '../../components/ConfirmDialog';
import {
  useDeleteFormMutation,
  useUpdateFormLocationStatusMutation,
} from '../../store/api/localApi';
import { FormType, FormStatus, FormDefinition, FormOwnerLevel } from '../../types';
import {
  PlusIcon,
  DocumentTextIcon,
  PencilIcon,
  DocumentDuplicateIcon,
  PaperAirplaneIcon,
  LockClosedIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import SendFormModal from '../../components/forms/SendFormModal';
import { FormDistributions } from './OrgFormManagementView';

// ── Constants ──────────────────────────────────────────────────────────────────

const formTypeLabels: Record<FormType, string> = {
  [FormType.REGISTRATION]: 'Registration Form',
  [FormType.CONSENT]: 'Consent Forms',
  [FormType.STUDENT_INTAKE]: 'Student Intake Forms',
};

const statusColors: Record<FormStatus, string> = {
  [FormStatus.DRAFT]: 'bg-yellow-100 text-yellow-800',
  [FormStatus.ACTIVE]: 'bg-green-100 text-green-800',
  [FormStatus.ARCHIVED]: 'bg-gray-100 text-gray-800',
};

/** Ordered list of form type sections */
const FORM_TYPE_ORDER = [FormType.REGISTRATION, FormType.CONSENT, FormType.STUDENT_INTAKE];

/**
 * Singleton types must always have one active form at a location — REGISTRATION
 * for student auto-create. CONSENT has no such constraint and (per the trigger
 * model) is always sendable from a published template, so no top-level
 * activate/deactivate toggle is shown.
 */
const SINGLETON_TYPES: FormType[] = [FormType.REGISTRATION];

const getFormIcon = (formType: FormType) => {
  switch (formType) {
    case FormType.REGISTRATION:
      return DocumentDuplicateIcon;
    default:
      return DocumentTextIcon;
  }
};

// ── Main View ──────────────────────────────────────────────────────────────────

export interface LocationFormViewProps {
  forms: FormDefinition[];
  isAdmin: boolean;
  isLocationAdmin: boolean;
}

const LocationFormView: React.FC<LocationFormViewProps> = ({ forms, isAdmin, isLocationAdmin }) => {
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [selectedFormForSend, setSelectedFormForSend] = useState<FormDefinition | null>(null);
  const [expandedDistributions, setExpandedDistributions] = useState<Record<string, boolean>>({});
  const [deleteForm] = useDeleteFormMutation();
  const [updateLocationStatus] = useUpdateFormLocationStatusMutation();
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const { effectiveLocationId } = useLocationContext();

  const groupedForms = useMemo(() => {
    const groups: Record<string, FormDefinition[]> = {};
    for (const type of FORM_TYPE_ORDER) {
      groups[type] = forms.filter((f) => f.formType === type);
    }
    return groups;
  }, [forms]);

  const toggleDistributions = (formId: string) => {
    setExpandedDistributions((prev) => ({ ...prev, [formId]: !prev[formId] }));
  };

  const isOrgForm = (form: FormDefinition) => form.ownerLevel === FormOwnerLevel.ORGANIZATION;
  const isReadOnlyForUser = (form: FormDefinition) =>
    isLocationAdmin && !isAdmin ? isOrgForm(form) : false;

  const handleDelete = (formId: string) => {
    setPendingDeleteId(formId);
  };

  const handleToggleLocationStatus = async (form: FormDefinition) => {
    if (!effectiveLocationId) return;
    const currentStatus = form.locationStatuses?.[effectiveLocationId] || form.status;
    const newStatus = currentStatus === FormStatus.ACTIVE ? FormStatus.DRAFT : FormStatus.ACTIVE;
    try {
      await updateLocationStatus({
        formId: form.id,
        locationId: effectiveLocationId,
        status: newStatus,
      }).unwrap();
    } catch (err: any) {
      // Surface the must-stay-active rule from the backend as a readable alert.
      // (Belt-and-braces: the UI also disables the deactivate button when the
      // form is the only active one of its singleton type.)
      const message = err?.data?.message || err?.message || 'Failed to update form status';
      window.alert(message);
    }
  };

  /**
   * For singleton form types (REGISTRATION) at a given location, count how
   * many forms of that type are currently active here.
   * If a form is the *only* active one of its singleton type, the deactivate
   * button is disabled with a tooltip — the backend would reject it anyway.
   */
  const isOnlyActiveSingleton = (form: FormDefinition): boolean => {
    if (!effectiveLocationId) return false;
    if (!SINGLETON_TYPES.includes(form.formType)) return false;
    const activeOfType = forms.filter((f) => {
      if (f.formType !== form.formType) return false;
      const s = f.locationId ? f.status : f.locationStatuses?.[effectiveLocationId];
      return s === FormStatus.ACTIVE;
    });
    return activeOfType.length === 1 && activeOfType[0].id === form.id;
  };

  const renderFormCard = (form: FormDefinition) => {
    const Icon = getFormIcon(form.formType);
    const readOnly = isReadOnlyForUser(form);
    const orgOwned = isOrgForm(form);
    const isExpanded = expandedDistributions[form.id] || false;

    // For org-wide forms, use per-location status if available
    const displayStatus =
      orgOwned && effectiveLocationId && form.locationStatuses?.[effectiveLocationId]
        ? form.locationStatuses[effectiveLocationId]
        : form.status;
    const isActiveAtLocation = displayStatus === FormStatus.ACTIVE;
    // Sendable templates show a Send button. CONSENT has no per-template
    // active gate — sendable as long as it's published here (lifecycle lives on
    // each distribution). STUDENT_INTAKE uses the standard rule: it must be
    // ACTIVE at this location. REGISTRATION (auto-triggered) is never
    // manually sent.
    const canSendForm =
      (form.formType === FormType.CONSENT && displayStatus !== FormStatus.ARCHIVED) ||
      (form.formType === FormType.STUDENT_INTAKE && isActiveAtLocation);

    return (
      <li key={form.id} data-testid={`form-card-${form.id}`}>
        <div className="px-4 py-4 sm:px-6 hover:bg-gray-50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center min-w-0">
              <div className="flex-shrink-0 hidden sm:block">
                <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <Icon className="h-5 w-5 text-primary-600" />
                </div>
              </div>
              <div className="sm:ml-4 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {form.title}
                  {form.title === 'Default Registration Form' && (
                    <span
                      data-testid={`form-default-badge-${form.id}`}
                      className="ml-2 inline-flex items-center rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700"
                    >
                      Default
                    </span>
                  )}
                </p>
                <div className="mt-1 flex items-center flex-wrap gap-1">
                  {/* CONSENT has no template-level lifecycle — show status for
                      every other type (including STUDENT_INTAKE). */}
                  {form.formType !== FormType.CONSENT && (
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[displayStatus]}`}
                    >
                      {displayStatus}
                    </span>
                  )}
                  <span
                    data-testid={`form-owner-badge-${form.id}`}
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      orgOwned ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'
                    }`}
                  >
                    {orgOwned ? (
                      <>
                        {readOnly && <LockClosedIcon className="h-3 w-3 mr-0.5" />}
                        Org
                      </>
                    ) : (
                      'Local'
                    )}
                  </span>
                  <span className="text-xs text-gray-400">
                    {form.sections?.flatMap((s) => s.fields).length ?? 0} fields · v{form.version}
                  </span>
                </div>
                {orgOwned && readOnly && (
                  <p className="mt-1 text-xs text-gray-400 italic">Managed by organization</p>
                )}
                {form.description && !readOnly && (
                  <p className="mt-1 text-sm text-gray-500 truncate max-w-xl">{form.description}</p>
                )}
              </div>
            </div>
            <div className="flex items-center flex-wrap gap-2">
              {/* Activate/Deactivate — for singleton types (REGISTRATION) and
                  STUDENT_INTAKE, which has a real per-template lifecycle and
                  must be ACTIVE to be sendable.
                  CONSENT is excluded — it's always sendable once published;
                  lifecycle lives on each distribution.
                  Singleton must-stay-active rule: the only active form of a
                  singleton type cannot be deactivated until a replacement is
                  activated (backend enforces; we disable the button as a hint).
                  STUDENT_INTAKE has no such constraint — toggle freely. */}
              {isAdmin &&
                !readOnly &&
                (SINGLETON_TYPES.includes(form.formType) ||
                  form.formType === FormType.STUDENT_INTAKE) &&
                (isActiveAtLocation ? (
                  isOnlyActiveSingleton(form) ? (
                    <button
                      data-testid={`form-deactivate-btn-${form.id}`}
                      disabled
                      title="Activate another form of this type before deactivating — one must always be active."
                      className="inline-flex items-center px-3 py-1.5 border border-gray-200 text-xs font-medium rounded text-gray-400 bg-gray-50 cursor-not-allowed"
                    >
                      <XMarkIcon className="h-4 w-4 mr-1" />
                      Deactivate
                    </button>
                  ) : (
                    <button
                      data-testid={`form-deactivate-btn-${form.id}`}
                      onClick={() => handleToggleLocationStatus(form)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-yellow-700 bg-yellow-50 hover:bg-yellow-100"
                    >
                      <XMarkIcon className="h-4 w-4 mr-1" />
                      Deactivate
                    </button>
                  )
                ) : (
                  <button
                    data-testid={`form-activate-btn-${form.id}`}
                    onClick={() => handleToggleLocationStatus(form)}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200"
                  >
                    <CheckIcon className="h-4 w-4 mr-1" />
                    Activate
                  </button>
                ))}

              {/* Send — for CONSENT (event-driven) and STUDENT_INTAKE (intake
                  extras). REGISTRATION fires automatically. */}
              {isAdmin && canSendForm && (
                <button
                  data-testid={`form-send-btn-${form.id}`}
                  onClick={() => {
                    setSelectedFormForSend(form);
                    setSendModalOpen(true);
                  }}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200"
                >
                  <PaperAirplaneIcon className="h-4 w-4 mr-1" />
                  Send
                </button>
              )}

              {isAdmin && (
                <>
                  {/*
                    A "Responses" link used to sit here pointing at
                    /forms/:formId/responses — a route that is NOT registered in
                    App.tsx and a page that was never built. Clicking it fell
                    through to a silent redirect to /dashboard, so the button
                    read as broken rather than as "not built yet".

                    Removed rather than pointed somewhere else: submitted
                    registration responses ARE viewable per-student via
                    components/students/RegistrationSection, and a per-form
                    responses list is genuine feature work (columns, filters,
                    PII handling) rather than a bug fix. The RTK hooks
                    (getFormResponses / getFormResponse) already exist for
                    whoever builds it.
                  */}

                  {/* Distributions toggle */}
                  <button
                    onClick={() => toggleDistributions(form.id)}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                  >
                    {isExpanded ? (
                      <ChevronUpIcon className="h-4 w-4 mr-1" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4 mr-1" />
                    )}
                    Distributions
                  </button>
                </>
              )}

              {/* Edit & Delete - only for location-owned forms. The seeded
                  Default Registration Form is the system's canonical baseline
                  per location; deleting it would leave the location with no
                  active REGISTRATION form, so the UI blocks it here. (The
                  backend should reject too — flagged as a follow-up.) */}
              {isAdmin && !readOnly && !orgOwned && (
                <>
                  <ScopedLink
                    to={`/forms/${form.id}/edit`}
                    data-testid={`form-edit-${form.id}`}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <PencilIcon className="h-4 w-4 mr-1" />
                    Edit
                  </ScopedLink>
                  <button
                    onClick={() => handleDelete(form.id)}
                    disabled={form.title === 'Default Registration Form'}
                    title={
                      form.title === 'Default Registration Form'
                        ? 'Default Registration Form cannot be deleted.'
                        : undefined
                    }
                    data-testid={`form-delete-${form.id}`}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <TrashIcon className="h-4 w-4 mr-1" />
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
        {/* Expanded distributions section */}
        {isAdmin && isExpanded && (
          <FormDistributions formId={form.id} currentFormVersion={form.version} />
        )}
      </li>
    );
  };

  return (
    <>
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Forms
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {isAdmin
              ? 'Manage and distribute forms at this location.'
              : 'View and fill out available forms.'}
          </p>
        </div>
        {isAdmin && (
          <div className="mt-4 flex md:mt-0 md:ml-4">
            <ScopedLink
              to="/forms/new"
              data-testid="forms-create-btn"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Create Form
            </ScopedLink>
          </div>
        )}
      </div>

      {/* Grouped form sections */}
      <div className="mt-6 space-y-8">
        {FORM_TYPE_ORDER.map((type) => {
          const typeForms = groupedForms[type] || [];
          if (typeForms.length === 0) return null;

          return (
            <div key={type} data-testid={`form-section-${type}`}>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">{formTypeLabels[type]}</h3>
              <div className="bg-white shadow overflow-hidden sm:rounded-md">
                <ul className="divide-y divide-gray-200">
                  {typeForms.map((form) => renderFormCard(form))}
                </ul>
              </div>
            </div>
          );
        })}

        {forms.length === 0 && (
          <div className="text-center py-12 bg-white shadow rounded-lg">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No forms found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {isAdmin
                ? 'Get started by creating a new form.'
                : 'No forms are available for you at this time.'}
            </p>
            {isAdmin && (
              <div className="mt-6">
                <ScopedLink
                  to="/forms/new"
                  data-testid="forms-create-btn-empty"
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                >
                  <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                  Create Form
                </ScopedLink>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Send Form Modal */}
      {selectedFormForSend && (
        <SendFormModal
          isOpen={sendModalOpen}
          onClose={() => {
            setSendModalOpen(false);
            setSelectedFormForSend(null);
          }}
          formId={selectedFormForSend.id}
          formTitle={selectedFormForSend.title}
          formType={selectedFormForSend.formType}
          locationId={selectedFormForSend.locationId || effectiveLocationId || ''}
        />
      )}
      <ConfirmDialog
        isOpen={!!pendingDeleteId}
        onConfirm={async () => {
          if (pendingDeleteId) {
            await deleteForm(pendingDeleteId);
          }
          setPendingDeleteId(null);
        }}
        onCancel={() => setPendingDeleteId(null)}
        title="Delete Form"
        message="Are you sure you want to delete this form?"
        confirmLabel="Delete"
        variant="danger"
      />
    </>
  );
};

export default LocationFormView;
